import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Property, PropertyType, PropertyPurpose } from '@/types/property';

// Sanitize search input to prevent PostgREST filter injection
function sanitizeSearchInput(input: string): string {
  // Remove PostgREST operators and special characters that could be used for injection
  // Allowed: letters, numbers, spaces, common accented characters
  const sanitized = input
    .replace(/[.,()]/g, ' ') // Replace operators with spaces
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .trim()
    .slice(0, 100); // Limit length
  
  // Additional validation: reject if contains PostgREST operators
  const dangerousPatterns = /\b(eq|neq|gt|gte|lt|lte|like|ilike|is|in|cs|cd|sl|sr|nxl|nxr|adj|ov|fts|plfts|phfts|wfts|not|and|or)\./i;
  if (dangerousPatterns.test(sanitized)) {
    return '';
  }
  
  return sanitized;
}

export interface PropertyFilters {
  type?: PropertyType;
  purpose?: PropertyPurpose;
  city?: string;
  neighborhood?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  minArea?: number;
  maxArea?: number;
  featured?: boolean;
  search?: string;
}

interface UsePropertiesOptions {
  filters?: PropertyFilters;
  page?: number;
  pageSize?: number;
  orderBy?: 'price' | 'created_at' | 'views_count';
  orderDirection?: 'asc' | 'desc';
}

export function useProperties(options: UsePropertiesOptions = {}) {
  const { 
    filters = {}, 
    page = 1, 
    pageSize = 12,
    orderBy = 'created_at',
    orderDirection = 'desc'
  } = options;

  return useQuery({
    queryKey: ['properties', filters, page, pageSize, orderBy, orderDirection],
    queryFn: async () => {
      // Use properties_public view to hide internal fields like created_by
      let query = supabase
        .from('properties_public')
        .select('*, property_images(*)', { count: 'exact' });

      // Apply filters
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.purpose) {
        query = query.eq('purpose', filters.purpose);
      }
      if (filters.city) {
        query = query.ilike('city', `%${filters.city}%`);
      }
      if (filters.neighborhood) {
        query = query.ilike('neighborhood', `%${filters.neighborhood}%`);
      }
      if (filters.minPrice) {
        query = query.gte('price', filters.minPrice);
      }
      if (filters.maxPrice) {
        query = query.lte('price', filters.maxPrice);
      }
      if (filters.bedrooms) {
        query = query.gte('bedrooms', filters.bedrooms);
      }
      if (filters.minArea) {
        query = query.gte('total_area', filters.minArea);
      }
      if (filters.maxArea) {
        query = query.lte('total_area', filters.maxArea);
      }
      if (filters.featured) {
        query = query.eq('is_featured', true);
      }
      if (filters.search) {
        const sanitizedSearch = sanitizeSearchInput(filters.search);
        if (sanitizedSearch) {
          query = query.or(`title.ilike.%${sanitizedSearch}%,description.ilike.%${sanitizedSearch}%,city.ilike.%${sanitizedSearch}%,neighborhood.ilike.%${sanitizedSearch}%`);
        }
      }

      // Apply ordering - featured properties first, then by selected order
      query = query
        .order('is_featured', { ascending: false })
        .order(orderBy, { ascending: orderDirection === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        properties: data as Property[],
        total: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
      };
    },
  });
}

export function useFeaturedProperties(limit = 6) {
  return useQuery({
    queryKey: ['featured-properties', limit],
    queryFn: async () => {
      // Use properties_public view to hide internal fields like created_by
      const { data, error } = await supabase
        .from('properties_public')
        .select('*, property_images(*)')
        .eq('is_featured', true)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Property[];
    },
  });
}

export function useProperty(idOrSlug: string) {
  return useQuery({
    queryKey: ['property', idOrSlug],
    queryFn: async () => {
      // Use properties_public view to hide internal fields like created_by
      // Try by slug first, then by ID
      let query = supabase
        .from('properties_public')
        .select('*, property_images(*)')
        .eq('slug', idOrSlug)
        .single();

      const { data: bySlug, error: slugError } = await query;

      if (!slugError && bySlug) {
        return bySlug as Property;
      }

      // Try by ID
      const { data, error } = await supabase
        .from('properties_public')
        .select('*, property_images(*)')
        .eq('id', idOrSlug)
        .single();

      if (error) throw error;
      return data as Property;
    },
    enabled: !!idOrSlug,
  });
}

export function useSimilarProperties(property: Property | undefined, limit = 4) {
  return useQuery({
    queryKey: ['similar-properties', property?.id],
    queryFn: async () => {
      if (!property) return [];

      // Use properties_public view to hide internal fields like created_by
      const { data, error } = await supabase
        .from('properties_public')
        .select('*, property_images(*)')
        .neq('id', property.id)
        .eq('type', property.type)
        .eq('purpose', property.purpose)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as Property[];
    },
    enabled: !!property,
  });
}
